import React, { useState, useEffect } from 'react';
import api from './axiosConfig';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  AlertTitle,
  LinearProgress,
  Divider,
  Grid,
  CircularProgress
} from '@mui/material';

const VatMonitoring = () => {
  const [vatStatus, setVatStatus] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define these functions before they're used
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(value);
  };

  const formatThreshold = (value) => {
    if (value === null || value === undefined) {
      return 'Not configured';
    }
    return formatCurrency(value);
  };

  const calculateProgressPercentage = (current, threshold) => {
    if (!current || !threshold) return 0;
    return Math.min(100, (current / threshold) * 100);
  };

  const getProgressColor = (percentage) => {
    if (percentage >= 100) return 'error';
    if (percentage >= 80) return 'warning';
    return 'primary';
  };

  // First fetch company profile to get VAT registration status
  useEffect(() => {
    const fetchCompanyProfile = async () => {
      try {
        const response = await api.get('/company/profile');
        setCompanyProfile(response.data);
      } catch (err) {
        console.error("Error fetching company profile:", err);
        setError("Failed to load company profile");
      }
    };

    fetchCompanyProfile();
  }, []);

  // Then fetch VAT thresholds and alerts
  useEffect(() => {
    const fetchVatStatus = async () => {
      if (!companyProfile) {
        return;
      }

      try {
        setLoading(true);

        const response = await api.get('/vat/status');

        if (response.data.success) {
          setVatStatus(response.data.data);
          setError(null);
        } else {
          // The route now correctly propagates the underlying service's
          // success flag (see vat_routes.py get_vat_status()), so this
          // branch can actually fire for a real failure (e.g. VAT
          // thresholds not configured) rather than never being reached.
          // The specific message lives on response.data.data.error_message
          // (set by check_vat_thresholds()) — response.data.error is not
          // a key either side of this contract actually sets, so it was
          // silently falling through to the generic fallback string.
          setError(
            response.data.error ||
            response.data.data?.error_message ||
            'Failed to load VAT data'
          );
        }
      } catch (err) {
        console.error('Error fetching VAT status:', err);
        setError('Failed to load VAT threshold information: ' + (err.response?.data?.error || err.message));
      } finally {
        setLoading(false);
      }
    };

    fetchVatStatus();
  }, [companyProfile]);

  if (loading && !companyProfile) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  // Determine VAT status from VAT status data, not company profile
  const isVatRegistered = vatStatus?.is_vat_registered === true || vatStatus?.is_vat_registered === 1;

  // If not VAT registered, show simplified view
  if (!isVatRegistered) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            VAT Status
          </Typography>
          <Alert severity="info">
            Your business is not currently VAT registered.
          </Alert>

          {vatStatus && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="subtitle1">
                12-Month Turnover: {formatCurrency(vatStatus.annual_turnover)}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Projected Annual: {formatCurrency(vatStatus.projected_annual)}
              </Typography>

              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  VAT Registration Threshold: {formatThreshold(vatStatus.registration_threshold)}
                </Typography>
                {vatStatus.registration_threshold !== null ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.registration_threshold)}
                        color={getProgressColor(calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.registration_threshold))}
                        sx={{ height: 10, borderRadius: 5 }}
                        aria-label={`Annual turnover progress toward VAT registration threshold: ${calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.registration_threshold)}%`}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.registration_threshold))}%
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    VAT thresholds are not configured. Contact support.
                  </Alert>
                )}
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>
    );
  }

  // If still loading VAT threshold data
  if (loading) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            VAT Monitoring
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={30} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  // If no VAT threshold data yet but we know we're VAT registered
  if (!vatStatus) {
    return (
      <Card sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            VAT Monitoring
          </Typography>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1">
              VAT Status: <strong>Registered</strong>
            </Typography>
            <Typography variant="subtitle2">
              VAT Number: {companyProfile.vat_number}
            </Typography>
            <Typography variant="subtitle2">
              VAT Scheme: {companyProfile.vat_scheme
                ? companyProfile.vat_scheme.replace(/_/g, ' ')
                : 'Not set'}
            </Typography>
          </Box>
          <Alert severity="info">
            Loading VAT threshold information...
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ mb: 4 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          VAT Monitoring
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1">
            VAT Status: <strong>Registered</strong>
          </Typography>
          <Typography variant="subtitle2">
            VAT Number: {vatStatus.vat_number || 'Not specified (needed to connect to HMRC)'}
          </Typography>
          <Typography variant="subtitle2">
            VAT Scheme: {vatStatus.vat_scheme
              ? vatStatus.vat_scheme.replace(/_/g, ' ')
              : 'Not set'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle1">
                12-Month Turnover: {formatCurrency(vatStatus.annual_turnover)}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary">
                Projected Annual: {formatCurrency(vatStatus.projected_annual)}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              You are already VAT registered, so the {formatThreshold(vatStatus.registration_threshold)} registration threshold no longer applies.
            </Typography>

            {(vatStatus.vat_scheme === 'FLAT_RATE' || vatStatus.vat_scheme === 'FIRST_YEAR_FLAT_RATE') && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Flat Rate Exit Threshold: {formatThreshold(vatStatus.flat_rate_exit_threshold)}
                </Typography>
                {vatStatus.flat_rate_exit_threshold !== null ? (
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ width: '100%', mr: 1 }}>
                      <LinearProgress
                        variant="determinate"
                        value={calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.flat_rate_exit_threshold)}
                        color={getProgressColor(calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.flat_rate_exit_threshold))}
                        sx={{ height: 10, borderRadius: 5 }}
                        aria-label={`Annual turnover progress toward Flat Rate scheme exit threshold: ${calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.flat_rate_exit_threshold)}%`}
                      />
                    </Box>
                    <Box sx={{ minWidth: 35 }}>
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(calculateProgressPercentage(vatStatus.annual_turnover, vatStatus.flat_rate_exit_threshold))}%
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Alert severity="error" sx={{ mt: 1 }}>
                    VAT thresholds are not configured. Contact support.
                  </Alert>
                )}
              </Box>
            )}

          </Grid>

          <Grid item xs={12} md={6}>
            {vatStatus.alerts && vatStatus.alerts.length > 0 ? (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  VAT Alerts
                </Typography>
                {vatStatus.alerts.map((alert, index) => (
                  <Alert
                    key={index}
                    severity={alert.level === 'danger' ? 'error' : 'warning'}
                    sx={{ mb: 2 }}
                  >
                    <AlertTitle>{alert.type.replace(/_/g, ' ')}</AlertTitle>
                    {alert.message}
                  </Alert>
                ))}
              </Box>
            ) : (
              <Box>
                <Typography variant="subtitle1" gutterBottom>
                  VAT Status
                </Typography>
                <Alert severity="success">
                  Your VAT status is healthy with no alerts.
                </Alert>
              </Box>
            )}
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default VatMonitoring;